import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Don't show if already installed, or dismissed within last 7 days
    if (standalone || (dismissed && daysSinceDismissed < 7)) {
      return;
    }

    // Listen for the beforeinstallprompt event (Chrome/Edge/etc)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay for better UX
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show custom instructions after delay
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('PWA install outcome:', outcome);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't render if already installed or shouldn't show
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-background border border-border rounded-2xl shadow-2xl z-50 overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
      {/* Header */}
      <div className="relative bg-gradient-to-r from-[#2D6A4F] to-[#40916c] p-4 text-white">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-white/20 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Smartphone className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Install Summary Maps</h3>
            <p className="text-sm text-white/80">Get the full app experience</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <ul className="space-y-2 mb-4 text-sm text-muted-foreground">
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 bg-[#2D6A4F]/10 rounded-full flex items-center justify-center text-[#2D6A4F] text-xs">✓</span>
            Quick access from your home screen
          </li>
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 bg-[#2D6A4F]/10 rounded-full flex items-center justify-center text-[#2D6A4F] text-xs">✓</span>
            Works offline with cached content
          </li>
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 bg-[#2D6A4F]/10 rounded-full flex items-center justify-center text-[#2D6A4F] text-xs">✓</span>
            Push notifications for study reminders
          </li>
        </ul>

        {isIOS ? (
          // iOS-specific instructions
          <div className="bg-muted/50 rounded-xl p-3 text-sm">
            <p className="font-medium mb-2">To install on iOS:</p>
            <ol className="space-y-1 text-muted-foreground">
              <li>1. Tap the <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-background rounded text-xs font-medium">Share <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg></span> button</li>
              <li>2. Scroll down and tap <strong>"Add to Home Screen"</strong></li>
              <li>3. Tap <strong>"Add"</strong> to confirm</li>
            </ol>
          </div>
        ) : (
          // Chrome/Edge install button
          <Button 
            onClick={handleInstall}
            className="w-full rounded-full bg-[#2D6A4F] hover:bg-[#1b4332]"
          >
            <Download className="w-4 h-4 mr-2" />
            Install App
          </Button>
        )}

        <button 
          onClick={handleDismiss}
          className="w-full mt-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}
